<script setup>
/**
 * Root Application Component
 * @description Main application wrapper with RouterView and global UI elements
 */

import { watch } from 'vue'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'

const authStore = useAuthStore()
const { setTheme } = useTheme()

/**
 * Sync backend preferences to localStorage (and apply theme) once the
 * user profile is available. Keeps the local cache current so that
 * components reading from localStorage see the right values on every
 * page load, even before auth completes on the next visit.
 */
watch(
  () => authStore.user?.preferences,
  (preferences) => {
    if (!preferences) return

    if (preferences.theme) {
      setTheme(preferences.theme)
    }
    if (preferences.notifications) {
      localStorage.setItem('notification_preferences', JSON.stringify(preferences.notifications))
    }
    if (preferences.display) {
      localStorage.setItem('display_preferences', JSON.stringify(preferences.display))
    }
  }
)
</script>

<template>
  <ErrorBoundary>
    <div id="app" class="min-h-screen">
      <!-- Main Router View - No transitions for faster navigation -->
      <RouterView />
    </div>
  </ErrorBoundary>
</template>

<style scoped>
/**
 * Ensure app takes full height
 */
#app {
  display: flex;
  flex-direction: column;
}
</style>
